import { findPayloadAdapter } from '@erp/json-renderer/apps';
import { resolveTemplate } from '@erp/shared-types';
import type { BehaviorPayloadMapping } from '@erp/shared-types';
import { AppActionError } from './appController';

/**
 * Turn a declared payload mapping into an operation's input. A dynamic
 * `$state` reference proves nothing about a value's type, so the adapter and
 * then the operation's own Zod schema validate the resolved value.
 */

function invalid(message: string): AppActionError {
  return new AppActionError({ reason: 'invalid', message });
}

export function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return null;
  return Object.fromEntries(Object.entries(value));
}

export function resolvePayload(
  mapping: BehaviorPayloadMapping,
  source: unknown,
): Record<string, unknown> {
  if (mapping.kind === 'discard') return {};
  const payload = asRecord(source) ?? {};
  if (mapping.kind === 'identity') return payload;
  return resolveAdapterPayload(mapping, payload);
}

function resolveAdapterPayload(
  mapping: Extract<BehaviorPayloadMapping, { kind: 'adapter' }>,
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const adapter = findPayloadAdapter(mapping.adapter);
  if (!adapter)
    throw invalid(`No registered payload adapter "${mapping.adapter}".`);
  const parsed = adapter.input.safeParse(
    resolveTemplate(mapping.input, payload),
  );
  if (!parsed.success)
    throw invalid(
      `The "${adapter.id}" payload does not match this operation: ${parsed.error.issues
        .map((issue) => issue.message)
        .join('; ')}`,
    );
  const resolved = asRecord(parsed.data);
  if (!resolved) throw invalid('A mapped payload must be an object.');
  return resolved;
}

/** Decode a JSON Pointer's segments, `~1` to `/` and `~0` to `~`. */
function pointerSegments(path: string): string[] | null {
  if (!path.startsWith('/')) return null;
  return path
    .slice(1)
    .split('/')
    .map((segment) => segment.replace(/~1/g, '/').replace(/~0/g, '~'));
}

/**
 * Whether `path` is `root` or sits inside it, by decoded segment. Comparing
 * string prefixes would let `/tasksOther` pass as inside `/tasks`.
 */
export function isWithin(path: string, root: string): boolean {
  const segments = pointerSegments(path);
  const rootSegments = pointerSegments(root);
  if (!segments || !rootSegments) return false;
  if (segments.length < rootSegments.length) return false;
  return rootSegments.every((segment, index) => segments[index] === segment);
}

/** The instance and state key an exact `/$behaviors/<instance>/<key>` path names. */
export function behaviorStateTarget(
  path: string,
): { instance: string; key: string } | null {
  const segments = pointerSegments(path);
  if (segments?.length !== 3 || segments[0] !== '$behaviors') return null;
  return { instance: segments[1], key: segments[2] };
}
